import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import HeroImage from "../assets/images/ai-sales-agent.jpg";
import { FooterPublic } from "./FooterPublic";

import {
  Car,
  MessageSquare,
  BarChart3,
  CheckCircle,
  Zap,
  Users,
  ShieldCheck,
} from "lucide-react";

const montserrat = {
  subsets: ["latin"],
  weight: ["400", "500", "600", "700", "800"],
};

export default function AISalesAgent() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Car className="text-teal-400 w-6 h-6" />,
      title: "Intelligent Model Discovery",
      desc: "Helps customers explore the right vehicles based on preferences and budget.",
    },
    {
      icon: <BarChart3 className="text-teal-400 w-6 h-6" />,
      title: "Structured Model Comparisons",
      desc: "Provides clear model-to-model and variant comparisons.",
    },
    {
      icon: <MessageSquare className="text-teal-400 w-6 h-6" />,
      title: "Accurate Query Handling",
      desc: "Answers detailed feature and specification questions instantly.",
    },
    {
      icon: <ShieldCheck className="text-teal-400 w-6 h-6" />,
      title: "Objection Handling",
      desc: "Handles concerns around pricing, service, warranty, charging networks.",
    },
    {
      icon: <Zap className="text-teal-400 w-6 h-6" />,
      title: "Decision Support",
      desc: "Summarizes differences and highlights value trade-offs.",
    },
    {
      icon: <Users className="text-teal-400 w-6 h-6" />,
      title: "Lead Capture",
      desc: "Converts conversations into enquiries, callbacks and test drive bookings.",
    },
  ];

  const problems = [
    "Customers research extensively online",
    "Most decisions are made before showroom visits",
    "Generic AI tools influence brand perception",
    "Static chatbots cannot guide purchase decisions",
  ];

  const benefits = [
    "Engage customers earlier in the buying journey",
    "Guide prospects with structured decision support",
    "Reduce hesitation through clear answers",
    "Convert website traffic into qualified leads",
  ];

  const agentic = [
    "Captures customer details automatically",
    "Generates digital enquiries",
    "Captures high-intent requests like test drives",
    "Initiates company follow-ups",
  ];

  return (
    <main
      className={`${montserrat.className} bg-gradient-to-b from-gray-900 to-black text-gray-100`}
    >
      <Header />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            AI Sales Agent for{" "}
            <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Automotive Brands
            </span>
          </h1>

          <p className="mt-6 text-lg text-gray-300 max-w-xl">
            Engage digital car buyers with an intelligent conversational agent
            that guides customers through discovery, comparison, and
            decision-making while converting engagement into qualified leads.
          </p>

          <div className="mt-8 flex gap-4">
            {/* <a
              href="#demo"
              className="px-6 py-3 bg-gradient-to-r from-teal-400 to-cyan-400 text-gray-900 font-semibold rounded-lg shadow-lg hover:scale-105 transition"
            >
              See Demo
            </a> */}

            <button
              onClick={() => navigate("/book-a-demo")}
              className="px-6 py-3 border border-gray-700 rounded-lg hover:bg-gray-800 transition"
            >
              Book a Demo
            </button>
          </div>
        </div>

        {/* Hero Image */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-cyan-500/20 blur-3xl"></div>

          <div className="relative rounded-xl overflow-hidden border border-gray-800">
            <img src={HeroImage} className="w-full" alt="AI Sales Agent demo" />
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl font-bold">
          The Car Buying Journey Has Changed
        </h2>

        <p className="text-gray-400 mt-4 max-w-3xl mx-auto text-lg">
          Today&apos;s customers complete most of their research online before
          entering a showroom. Generic AI tools and static chatbots cannot guide
          meaningful purchase decisions.
        </p>

        <div className="grid md:grid-cols-2 gap-8 mt-12 text-left">
          {problems.map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-4 bg-gray-900/60 p-6 rounded-xl border border-gray-800 hover:border-teal-400/40 transition"
            >
              <CheckCircle className="text-teal-400 w-6 h-6 mt-1" />
              <p className="text-gray-300">{item}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-center">
          What the AI Sales Agent Does
        </h2>

        <div className="grid md:grid-cols-3 gap-8 mt-14">
          {features.map((f, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -6 }}
              className="p-6 bg-gray-900/60 backdrop-blur border border-gray-800 rounded-xl hover:border-teal-400/40 transition"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gray-800 rounded-lg">{f.icon}</div>
                <h4 className="font-semibold text-lg">{f.title}</h4>
              </div>

              <p className="text-gray-400">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AGENTIC */}
      <section className="bg-gray-950 py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold">
            Beyond Chatbots: Agentic Capabilities
          </h2>

          <ul className="mt-10 grid md:grid-cols-2 gap-6 text-left">
            {agentic.map((item, i) => (
              <li key={i} className="flex gap-3 items-start">
                <Zap className="text-teal-400 w-5 h-5 mt-1" />
                <span className="text-gray-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-center">
          How It Helps Automotive Brands
        </h2>

        <div className="grid md:grid-cols-2 gap-8 mt-12">
          {benefits.map((item, i) => (
            <div
              key={i}
              className="flex gap-3 bg-gray-900/60 border border-gray-800 p-6 rounded-xl hover:border-teal-400/40 transition"
            >
              <CheckCircle className="text-teal-400 w-5 h-5 mt-1" />
              <span className="text-gray-300">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* DEPLOYMENT */}
      <section className="bg-gray-950 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center">
            Where It Can Be Deployed
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mt-12 text-lg">
            <div className="bg-gray-900/60 p-6 border border-gray-800 rounded-xl">
              Website chat widget
            </div>

            <div className="bg-gray-900/60 p-6 border border-gray-800 rounded-xl">
              Lead follow-up conversations
            </div>

            <div className="bg-gray-900/60 p-6 border border-gray-800 rounded-xl">
              Post enquiry engagement
            </div>

            <div className="bg-gray-900/60 p-6 border border-gray-800 rounded-xl">
              Digital product research journeys
            </div>
          </div>
        </div>
      </section>

      {/* DEMO VIDEO */}
      {/* <section id="demo" className="max-w-6xl mx-auto px-6 py-24 text-center">
        <h2 className="text-3xl font-bold">See the AI Sales Agent in Action</h2>

        <div className="mt-10 aspect-video rounded-xl overflow-hidden border border-gray-800">
          <iframe
            className="w-full h-full"
            src="https://www.youtube.com/embed/YOUR_VIDEO"
            allowFullScreen
          />
        </div>
      </section> */}

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto text-center bg-gray-900/60 border border-gray-800 rounded-2xl p-12 backdrop-blur">
          <h2 className="text-3xl font-bold">
            Bring an AI Sales Agent to Your Brand
          </h2>

          <p className="text-gray-400 mt-4">
            Transform digital engagement into qualified automotive leads.
          </p>

          <button
            onClick={() => navigate("/book-a-demo")}
            className="mt-8 px-8 py-4 bg-gradient-to-r from-teal-400 to-cyan-400 text-gray-900 font-semibold rounded-lg shadow-lg hover:scale-105 transition"
          >
            Book a Demo
          </button>
        </div>
      </section>

      <FooterPublic />
    </main>
  );
}
